import { CONFIG } from './config.js';

const TOKEN_KEY = 'mv_auth_token';
const USER_KEY = 'mv_auth_user';
const IDENTITY_KEY = 'mv_auth_identity';
const COMPANIES_KEY = 'mv_auth_companies';
/** Bedrijven waar een systeembeheerder alleen MAG MEEKIJKEN (leesrechten, geen lidmaatschap). */
const LOOKALONG_KEY = 'mv_auth_lookalong';
/** Gebeurtenis op `window` zodra de bedrijvenlijst verandert; de topbalk luistert ernaar. */
export const COMPANIES_EVENT = 'mv-companies-changed';

export class AuthService {
  constructor() {
    this.token = localStorage.getItem(TOKEN_KEY);
    this.user = JSON.parse(localStorage.getItem(USER_KEY) || 'null');
    this.identityToken = localStorage.getItem(IDENTITY_KEY);
    this.companies = JSON.parse(localStorage.getItem(COMPANIES_KEY) || '[]');
    this.lookAlongCompanies = JSON.parse(localStorage.getItem(LOOKALONG_KEY) || '[]');

    // Check if token is expired on initialization
    if (this.token && this.isTokenExpired()) {
      this.logout();
    }
  }

  isAuthenticated() {
    // Check both token existence and expiration
    return !!this.token && !this.isTokenExpired();
  }

  /**
   * Check if the token is expired by decoding the JWT payload
   */
  isTokenExpired() {
    return this.isExpired(this.token);
  }

  /**
   * Is dit token verlopen? (formaat: base64(payload).handtekening)
   *
   * Werkt op een MEEGEGEVEN token en niet alleen op `this.token`, want we hebben er twee: het
   * identiteitstoken (wie ben je, geen bedrijf) en het bedrijfstoken. Zie `_bearerForIdentity`
   * voor waarom dat verschil ertoe doet.
   */
  isExpired(token) {
    if (!token) return true;

    try {
      const [payloadB64] = token.split('.');
      if (!payloadB64) return true;

      const payload = JSON.parse(atob(payloadB64));

      // Met een minuut speling: een token dat over 30 seconden verloopt is halverwege het
      // verzoek alsnog te laat.
      const now = Date.now();
      const buffer = 60 * 1000;
      return now > (payload.exp - buffer);
    } catch (error) {
      console.error('Error checking token expiration:', error);
      return true; // Treat invalid tokens as expired
    }
  }

  /**
   * Welk token gebruiken we voor 'wie ben je'-verzoeken (bedrijvenlijst, bedrijf wisselen)?
   *
   * Het identiteitstoken bij voorkeur -- dat hoort bij de persoon en niet bij een bedrijf.
   * Maar het wordt ALLEEN bij inloggen uitgegeven en nooit ververst, terwijl het bedrijfstoken
   * bij elke wissel opnieuw wordt uitgedeeld. Beide leven zeven dagen, dus wie een week lang
   * blijft werken en af en toe van bedrijf wisselt, houdt een vers bedrijfstoken over en een
   * verlopen identiteitstoken. Dan werkte de hele applicatie nog, maar gaf uitgerekend
   * bedrijf wisselen 401 -- en dat is niet te raden voor wie het overkomt.
   *
   * Het bedrijfstoken is een prima terugval: de server leest daar dezelfde gebruiker uit en
   * controleert het lidmaatschap zelf.
   */
  _bearerForIdentity() {
    if (this.identityToken && !this.isExpired(this.identityToken)) return this.identityToken;
    return this.token;
  }

  getToken() {
    return this.token;
  }

  getUser() {
    return this.user;
  }

  async login(email, password) {
    try {
      const response = await fetch(`${CONFIG.API_BASE}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email, password })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Login mislukt');
      }

      const data = await response.json();

      // 2FA hand-off: password OK but a second factor / enrollment is required first.
      // No session is stored yet — the login form drives the next step.
      if (data.requiresMfa) return { requiresMfa: true, mfaToken: data.mfaToken };
      if (data.requiresMfaSetup) return { requiresMfaSetup: true, setupToken: data.setupToken };

      return this._storeSession(data);
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  /** Persist a full session response (from login or a completed 2FA step). */
  _storeSession(data) {
    this.token = data.token;                 // default scoped token (oldest company)
    this.user = data.user;
    this.identityToken = data.identityToken || null;

    localStorage.setItem(TOKEN_KEY, this.token);
    localStorage.setItem(USER_KEY, JSON.stringify(this.user));
    if (this.identityToken) localStorage.setItem(IDENTITY_KEY, this.identityToken);
    this._setCompanies(data.companies);
    // De meekijklijst komt niet mee met het inlogantwoord; die wordt opgehaald bij de eerste
    // verversing. Hier leegmaken, zodat de lijst van een vorige gebruiker niet blijft staan.
    this._setLookAlong([]);

    // When the user belongs to >1 company, the app must let them pick one first.
    return { success: true, needsCompanySelection: this.companies.length > 1, companies: this.companies };
  }

  async _post2fa(path, body) {
    const response = await fetch(`${CONFIG.API_BASE}/auth/2fa/${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.error || 'Verificatie mislukt');
    return data;
  }

  /** Second login step: verify the TOTP code against the mfaToken. */
  async verifyMfa(mfaToken, code) {
    try {
      const data = await this._post2fa('verify', { mfaToken, code });
      return this._storeSession(data);
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  /** Forced enrollment (sysadmin): fetch the QR/secret using the setup token. */
  async mfaSetupBegin(setupToken) {
    return this._post2fa('setup', { setupToken }); // { otpauthUri, secret }
  }

  /** Forced enrollment: confirm a code; on success a session is issued and stored. */
  async mfaSetupEnable(setupToken, code) {
    try {
      const data = await this._post2fa('enable', { setupToken, code });
      if (data.token) return this._storeSession(data);
      return { success: true, needsCompanySelection: false, companies: [] };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  /** Lost authenticator: e-mail a one-time recovery code. */
  async recoverMfaStart(mfaToken) {
    try { await this._post2fa('recover', { mfaToken }); return { success: true }; }
    catch (error) { return { success: false, error: error.message }; }
  }

  /** Consume the e-mailed recovery code: disables 2FA, then a session or forced re-enrollment. */
  async recoverMfaVerify(mfaToken, code) {
    try {
      const data = await this._post2fa('recover/verify', { mfaToken, code });
      if (data.requiresMfaSetup) return { requiresMfaSetup: true, setupToken: data.setupToken };
      return this._storeSession(data);
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  /** Companies the user can access (from the last login). */
  getCompanies() {
    return this.companies || [];
  }

  /**
   * Bedrijven waar deze systeembeheerder mag MEEKIJKEN: geen lidmaatschap, alleen leesrechten.
   * Staat los van getCompanies() zodat de kiezer ze apart kan tonen en niemand ze aanziet voor
   * een bedrijf waar hij werkelijk bij hoort.
   */
  getLookAlongCompanies() {
    return this.lookAlongCompanies || [];
  }

  /**
   * Kijk je op dit moment mee in een bedrijf waar je geen lid van bent?
   *
   * Alleen voor de weergave — de echte grens ligt in de backend, die elk schrijfverzoek van
   * zo'n sessie weigert. Dit voorkomt dat je knoppen te zien krijgt die toch een 403 opleveren.
   */
  isLookAlongSession() {
    return !!(this.user && this.user.look_along);
  }

  /** The currently active company (from the scoped token / user). */
  getActiveCompany() {
    if (!this.user) return null;
    return { id: this.user.company_id, name: this.user.company_name, role: this.user.company_role };
  }

  isSystemAdmin() {
    return !!(this.user && this.user.is_system_admin);
  }

  /**
   * Bewaar de bedrijvenlijst en laat de app weten dat hij veranderd is.
   *
   * De bedrijvenkiezer in de topbalk leest deze lijst bij het renderen. Werd hij alleen
   * bijgewerkt, dan bleef de oude keuzelijst staan tot de pagina toevallig opnieuw tekende --
   * en zag je een bedrijf waar je zojuist aan toegevoegd was pas na uit- en inloggen.
   */
  _setCompanies(list) {
    const nieuw = Array.isArray(list) ? list : [];
    // Ook de rol telt: word je bij een bedrijf beheerder, dan is de lijst van id's gelijk
    // maar het label in de kiezer niet. Vergeleek dit alleen id's, dan bleef daar 'LID'
    // staan -- ook na een verversing, want de nieuwe lijst werd wel bewaard maar de topbalk
    // hoorde het niet.
    const kenmerk = (list) => JSON.stringify((list || []).map((c) => [c.id, c.name, c.role]));
    const veranderd = kenmerk(nieuw) !== kenmerk(this.companies);
    this.companies = nieuw;
    localStorage.setItem(COMPANIES_KEY, JSON.stringify(this.companies));
    if (veranderd) window.dispatchEvent(new CustomEvent(COMPANIES_EVENT, { detail: this.companies }));
    return this.companies;
  }

  /** Zelfde opzet als _setCompanies, voor de meekijklijst. */
  _setLookAlong(list) {
    this.lookAlongCompanies = Array.isArray(list) ? list : [];
    localStorage.setItem(LOOKALONG_KEY, JSON.stringify(this.lookAlongCompanies));
    return this.lookAlongCompanies;
  }

  /** Re-fetch the companies the user can access (e.g. after a sysadmin self-joins). */
  async refreshCompanies() {
    try {
      const bearer = this._bearerForIdentity();
      const response = await fetch(`${CONFIG.API_BASE}/auth/companies`, {
        headers: { Authorization: `Bearer ${bearer}` },
      });
      if (response.ok) {
        const data = await response.json();
        this._setCompanies(data.companies);
        this._setLookAlong(data.lookAlong);
        // Je rol staat óók in het bedrijfstoken, en dáár kijkt de server naar. Is die bij
        // het actieve bedrijf intussen anders (iemand maakte je beheerder), dan zou de kiezer
        // 'BEHEERDER' zeggen terwijl elke aanroep nog als lid binnenkomt — tot je opnieuw
        // inlogt. Daarom hier stil hetzelfde bedrijf opnieuw kiezen: dat levert een vers token.
        const actief = this.user && !this.user.look_along
          ? (this.companies || []).find((c) => c.id === this.user.company_id) : null;
        if (actief && actief.role && actief.role !== this.user.company_role) {
          await this.selectCompany(actief.id);
        }
      }
    } catch { /* ignore */ }
    return this.companies;
  }

  /** Switch to another company the user is a member of (membership-checked server-side). */
  async selectCompany(companyId) {
    try {
      const bearer = this._bearerForIdentity();
      const response = await fetch(`${CONFIG.API_BASE}/auth/select-company`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${bearer}` },
        body: JSON.stringify({ companyId }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || 'Bedrijf wisselen mislukt');
      }
      const data = await response.json();
      this.token = data.token;
      this.user = data.user;
      localStorage.setItem(TOKEN_KEY, this.token);
      localStorage.setItem(USER_KEY, JSON.stringify(this.user));
      // De server geeft hier een vers identiteitstoken mee, zodat het meeleeft met de sessie
      // in plaats van zeven dagen na het inloggen om te vallen. Oudere servers sturen het niet;
      // dan houden we gewoon het bestaande.
      if (data.identityToken) {
        this.identityToken = data.identityToken;
        localStorage.setItem(IDENTITY_KEY, this.identityToken);
      }
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  logout() {
    this.token = null;
    this.user = null;
    this.identityToken = null;
    this.companies = [];
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem(IDENTITY_KEY);
    localStorage.removeItem(COMPANIES_KEY);
  }

  setTokenAndUser(token, user) {
    this.token = token;
    this.user = user;
    localStorage.setItem(TOKEN_KEY, this.token);
    localStorage.setItem(USER_KEY, JSON.stringify(this.user));
  }
}

export const auth = new AuthService();
